import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { fetchGroups } from "@/api/groups";
import { sendBroadcast } from "@/api/notifications";
import { extractErrorMessage } from "@/types/common";
import type { BroadcastAudience, BroadcastChannel } from "@/types/notification";

const AUDIENCE_LABELS: Record<BroadcastAudience, string> = {
  all_active: "Все активные ученики",
  debtors: "Только должники",
  group: "Ученики группы",
  leads: "Заявки (лиды)",
};

const CHANNEL_LABELS: Record<BroadcastChannel, string> = {
  auto: "Авто (Telegram, иначе SMS)",
  sms: "Только SMS",
  telegram: "Только Telegram",
};

export function BroadcastTab() {
  const [audience, setAudience] = useState<BroadcastAudience>("all_active");
  const [groupId, setGroupId] = useState("");
  const [channel, setChannel] = useState<BroadcastChannel>("auto");
  const [text, setText] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: groups } = useQuery({
    queryKey: ["groups", "for-broadcast"],
    queryFn: () => fetchGroups({ status: "ACTIVE", limit: 200 }),
    enabled: audience === "group",
  });

  const mutation = useMutation({
    mutationFn: () => sendBroadcast({ audience, groupId: audience === "group" ? groupId : undefined, channel, text }),
    onSuccess: (data) => {
      setResult(`Поставлено в очередь: ${data.queued} сообщений.`);
      setServerError(null);
      setText("");
    },
    onError: (error) => {
      setServerError(extractErrorMessage(error));
      setResult(null);
    },
  });

  return (
    <Stack spacing={2} maxWidth={520}>
      {result && <Alert severity="success">{result}</Alert>}
      {serverError && <Alert severity="error">{serverError}</Alert>}
      <TextField select label="Получатели" value={audience} onChange={(e) => setAudience(e.target.value as BroadcastAudience)}>
        {Object.entries(AUDIENCE_LABELS).map(([key, label]) => (
          <MenuItem key={key} value={key}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      {audience === "group" && (
        <TextField select label="Группа" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
          {(groups?.items ?? []).map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.code} — {g.subjectName ?? g.subjectId}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField select label="Канал" value={channel} onChange={(e) => setChannel(e.target.value as BroadcastChannel)}>
        {Object.entries(CHANNEL_LABELS).map(([key, label]) => (
          <MenuItem key={key} value={key}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField label="Текст сообщения" value={text} onChange={(e) => setText(e.target.value)} multiline minRows={4} />
      <Button
        variant="contained"
        disabled={!text || (audience === "group" && !groupId) || mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        Отправить рассылку
      </Button>
    </Stack>
  );
}
